import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Stack } from 'new/elements/Stack';
import Typography from 'new/elements/TypographySupport/TypographySupport';
import { TABLE_NEST_INDENT_PX } from 'js/constants/utilityConstants';

interface EndpointParam {
  key: string;
  value: string;
  required: boolean;
}

interface ParameterTableProps {
  params?: EndpointParam[];
  body?: any;
  title: string;
  subtitle?: string;
}

/**
 * Extracts an object value from various input types
 * Returns the object if valid, or null if not expandable
 */
function getObjectValue(value: any): any {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value;
  }

  // If it's a string that looks like JSON, try to parse it
  if (typeof value === 'string' && value.startsWith('{')) {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (e) {
      return null;
    }
  }

  return null;
}

function isExpandableValue(value: any): boolean {
  return getObjectValue(value) !== null;
}

interface ParameterRowProps {
  paramKey: string;
  value: any;
  required: boolean;
  depth?: number;
}

function ParameterRow({ paramKey, value, required, depth = 0 }: ParameterRowProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const isExpandable = isExpandableValue(value);
  const objectValue = isExpandable ? getObjectValue(value) : null;
  const indentSize = depth * TABLE_NEST_INDENT_PX;

  return (
    <>
      <TableRow>
        <TableCell>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              pl: `${indentSize}px`,
            }}
          >
            <Typography variant="body2">{paramKey}</Typography>
            {isExpandable && (
              <IconButton
                size="small"
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{
                  padding: 0,
                  marginLeft: 0.5,
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                  '& svg': {
                    fill: 'var(--color-greyscale-800)',
                  },
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            )}
            {required && (
              <Chip
                label="REQUIRED"
                size="small"
                color="error"
                variant="outlined"
                sx={{ height: '20px', fontSize: '0.65rem' }}
              />
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {isExpandable
              ? '{...}'
              : value !== null && value !== undefined && value !== ''
                ? String(value)
                : '-'}
          </Typography>
        </TableCell>
      </TableRow>
      {isExpandable && objectValue && isExpanded && (
        <>
          {Object.entries(objectValue).map(([nestedKey, nestedValue], index) => (
            <ParameterRow
              key={index}
              paramKey={nestedKey}
              value={nestedValue}
              required={false}
              depth={depth + 1}
            />
          ))}
        </>
      )}
    </>
  );
}

/**
 * ParameterTable component for displaying API endpoint parameters in a table format.
 *
 * @param params - Array of endpoint parameters (for headers, path params, query params)
 * @param body - JSON body object to be converted to table rows
 * @param title - Section title (e.g., "Headers", "Request Body")
 * @param subtitle - Optional subtitle (e.g., "application/json")
 */
export default function ApiServerParameterTable({
  params,
  body,
  title,
  subtitle,
}: ParameterTableProps) {
  // Convert body object to params format if provided
  const tableParams = React.useMemo(() => {
    if (body) {
      // Parse body schema if it's a string
      let bodySchema = body;
      if (typeof body === 'string') {
        try {
          bodySchema = JSON.parse(body);
        } catch (e) {
          return [];
        }
      }

      if (!bodySchema || typeof bodySchema !== 'object' || Array.isArray(bodySchema)) {
        return [];
      }

      return Object.entries(bodySchema).map(([key, value]) => ({
        key,
        value: typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value),
        required: false,
      }));
    }
    return params || [];
  }, [params, body]);

  if (!tableParams || tableParams.length === 0) return null;

  return (
    <Box>
      <Stack gap="8px" direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">{title}</Typography>
        {subtitle && <Typography variant="body2">{subtitle}</Typography>}
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'var(--color-galactic-sand-bg)' }}>
              <TableCell sx={{ width: '50%' }}>
                <Typography variant="body2" fontWeight="bold">
                  Name
                </Typography>
              </TableCell>
              <TableCell sx={{ width: '50%' }}>
                <Typography variant="body2" fontWeight="bold">
                  Sample Value
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableParams.map((param, index) => (
              <ParameterRow
                key={index}
                paramKey={param.key}
                value={param.value}
                required={param.required}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
